// react
import { useId, useMemo, useRef } from 'react';

// material ui
import { Box, Dialog, DialogContent, DialogTitle } from '@mui/material';
import { areaElementClasses } from '@mui/x-charts/LineChart';
import { SparkLineChart } from '@mui/x-charts/SparkLineChart';

// api
import type { TimeSeriesDataPoint } from '@/api/generated/energyAnalysis/endpoints/portfolioAnalysis';

// components
import useHighchartsThemeSync from '@/hooks/useHighchartsThemeSync';
import { formatNumber } from '@/utils/formatters';

import Highcharts, { merge } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { DateTime } from 'luxon';

interface PortfolioKpiSparklineProps {
  dataPoints: TimeSeriesDataPoint[];
  unit?: string;
  color: string;
}

interface PortfolioKpiSparklineDialogProps {
  open: boolean;
  onClose: () => void;
  dataPoints?: TimeSeriesDataPoint[];
  unit?: string;
  color: string;
  label?: string;
}

interface AreaGradientProps {
  color: string;
  id: string;
}

interface GroupedDataPoint {
  timestamp: string;
  value: number;
}

const groupDataByWeek = (data: TimeSeriesDataPoint[]): GroupedDataPoint[] => {
  if (!data || data.length === 0) {
    return [];
  }

  const grouped = data.reduce<Record<string, GroupedDataPoint>>(
    (accumulator, { timestamp, value }) => {
      const weekStart = DateTime.fromISO(timestamp)
        .setZone('Europe/Copenhagen')
        .startOf('week');
      const weekKey = weekStart.toISODate();

      if (!weekKey) {
        return accumulator;
      }

      accumulator[weekKey] = {
        timestamp: weekStart.toISO() ?? timestamp,
        value: (accumulator[weekKey]?.value ?? 0) + value,
      };

      return accumulator;
    },
    {},
  );

  return Object.values(grouped).sort(
    (left, right) =>
      DateTime.fromISO(left.timestamp).toMillis() -
      DateTime.fromISO(right.timestamp).toMillis(),
  );
};

const formatWeekRangeLabel = (timestamp: string): string => {
  const start = DateTime.fromISO(timestamp)
    .setZone('Europe/Copenhagen')
    .setLocale('da-dk');
  const end = start.endOf('week');
  const weekNumber = start.weekNumber;

  return `Uge ${weekNumber}: ${start.toFormat('dd MMM yy')} - ${end.toFormat(
    'dd MMM yy',
  )}`;
};

const AreaGradient = ({ color, id }: AreaGradientProps) => (
  <defs>
    <linearGradient id={id} x1="50%" y1="0%" x2="50%" y2="100%">
      <stop offset="0%" stopColor={color} stopOpacity={0.3} />
      <stop offset="100%" stopColor={color} stopOpacity={0} />
    </linearGradient>
  </defs>
);

export const PortfolioKpiSparkline = ({
  dataPoints,
  unit,
  color,
}: PortfolioKpiSparklineProps) => {
  const groupedData = useMemo(() => groupDataByWeek(dataPoints), [dataPoints]);
  const sparklineEntries =
    groupedData.length > 2 ? groupedData.slice(1, -1) : groupedData;
  const sparklineData = sparklineEntries
    .map((entry) => Number.parseInt(String(entry.value), 10))
    .filter((entry) => Number.isFinite(entry));
  const tooltipLabels = sparklineEntries.map((entry) =>
    formatWeekRangeLabel(entry.timestamp),
  );
  const gradientId = useId().replace(/:/g, '_');

  if (sparklineData.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        borderRadius: 1,
      }}
    >
      <SparkLineChart
        data={sparklineData}
        height={50}
        color={color}
        showTooltip
        showHighlight
        xAxis={{
          scaleType: 'band',
          data: tooltipLabels,
        }}
        area
        curve="monotoneX"
        valueFormatter={(value: number | null) =>
          `${formatNumber(value ?? undefined)} ${unit ?? ''}`
        }
        sx={{
          [`& .${areaElementClasses.root}`]: {
            fill: `url(#${gradientId})`,
            stroke: 'none',
          },
        }}
      >
        <AreaGradient color={color} id={gradientId} />
      </SparkLineChart>
    </Box>
  );
};

export const PortfolioKpiSparklineDialog = ({
  open,
  onClose,
  dataPoints,
  unit,
  color,
  label,
}: PortfolioKpiSparklineDialogProps) => {
  const chartRef = useRef<HighchartsReact.RefObject | null>(null);
  const initialTheme = useHighchartsThemeSync(chartRef, false);
  const groupedData = useMemo(
    () => groupDataByWeek(dataPoints ?? []),
    [dataPoints],
  );

  const seriesData = useMemo(
    () =>
      groupedData
        .map((entry) => {
          const pointDate = DateTime.fromISO(entry.timestamp).setZone(
            'Europe/Copenhagen',
          );

          if (!pointDate.isValid) {
            return null;
          }

          return {
            x: pointDate.toUTC().toMillis(),
            y: entry.value,
            weekLabel: formatWeekRangeLabel(entry.timestamp),
          };
        })
        .filter(
          (entry): entry is { x: number; y: number; weekLabel: string } =>
            entry != null,
        ),
    [groupedData],
  );

  const chartOptions = useMemo(
    () =>
      merge(initialTheme, {
        chart: {
          height: 420,
          spacingLeft: 16,
          spacingRight: 16,
        },
        credits: { enabled: false },
        title: { text: '' },
        legend: { enabled: false },
        xAxis: merge(initialTheme?.xAxis ?? {}, {
          type: 'datetime',
          tickLength: 0,
        }),
        yAxis: [
          merge(initialTheme?.yAxis?.[0] ?? {}, {
            title: {
              text: unit ?? null,
            },
            min: 0,
          }),
        ],
        tooltip: merge(initialTheme?.tooltip ?? {}, {
          headerFormat: '{point.weekLabel}<br/>',
          pointFormatter: function (this: Highcharts.Point) {
            return `<span style="color:${color}">●</span> ${formatNumber(this.y)} ${unit ?? ''}`;
          },
        }),
        plotOptions: {
          series: {
            marker: {
              enabled: true,
              radius: 3,
            },
          },
        },
        series: [
          {
            type: 'line',
            name: label,
            data: seriesData,
            color,
          },
        ],
      }),
    [color, initialTheme, label, seriesData, unit],
  );

  if (!Array.isArray(dataPoints) || dataPoints.length === 0) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
      <DialogTitle>{label}</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ width: '100%' }}>
          <HighchartsReact
            highcharts={Highcharts}
            options={chartOptions}
            ref={chartRef}
          />
        </Box>
      </DialogContent>
    </Dialog>
  );
};
